/** RSVP, Guest List & Walk-in domain types (Sprint 29 + Sprint 5 Aug 26). */

export type TicketTemplateId = "black" | "white";

export type PromoterDisplayMode = "hidden" | "collapsible" | "bold";

export type TicketTemplateConfig = {
  show_banner: boolean;
  /** Same hidden/collapsible/bold behaviour as promoter_display, applied to the same effect on all 3 public forms (RSVP, Guest List, Walk-in). */
  lineup_display: PromoterDisplayMode;
  promoter_display: PromoterDisplayMode;
  banner_url?: string | null;
};

export const DEFAULT_TICKET_TEMPLATE_CONFIG: TicketTemplateConfig = {
  show_banner: true,
  lineup_display: "collapsible",
  promoter_display: "collapsible",
  banner_url: null,
};

export const TICKET_TEMPLATE_LABELS: Record<TicketTemplateId, string> = {
  black: "Black",
  white: "White",
};

export type EventPromoter = {
  id: string;
  event_id: string;
  name: string;
  guest_count?: number;
  created_at?: string;
};

export type RsvpCategory = "stag" | "couple" | "table";

export const RSVP_CATEGORY_LABELS: Record<RsvpCategory, string> = {
  stag: "Stag",
  couple: "Couple",
  table: "Table",
};

/** Default ticket-equivalent pricing used for projected RSVP revenue. */
export const DEFAULT_RSVP_CATEGORY_PRICES: Record<RsvpCategory, number> = {
  stag: 1_500,
  couple: 2_500,
  table: 10_000,
};

/** Per-tier counts keyed by tier id (preferred) or tier name. */
export type TierCountMap = Record<string, number>;

/** How a public form takes money: free (guest list), online, online or at the gate, or only at the gate. */
export type FormPayment = "free" | "online" | "online_or_gate" | "gate";
export type PayPreference = "online" | "gate";
export type GuestPaymentStatus = "none" | "pending" | "paid" | "due" | "refunded";

export const FORM_PAYMENT_LABELS: Record<FormPayment, string> = {
  free: "Free",
  online: "Pay online",
  online_or_gate: "Online or at the gate",
  gate: "Pay at the gate",
};

export type GuestSource = "rsvp" | "guest_list" | "walk_in" | "manual" | "zoniq_online";
/** Which list the guest belongs to (RSVP vs Guest List vs Walk-in). */
export type GuestChannel = "rsvp" | "guest_list" | "walk_in" | "zoniq_online";

export const GUEST_CHANNEL_LABELS: Record<GuestChannel, string> = {
  rsvp: "RSVP",
  guest_list: "Guest List",
  walk_in: "Walk-in",
  zoniq_online: "Zoniq ticket",
};

export const GUEST_SOURCE_LABELS: Record<GuestSource, string> = {
  rsvp: "RSVP link",
  guest_list: "Guest List link",
  walk_in: "Walk-in",
  manual: "Manual",
  zoniq_online: "Zoniq ticket",
};

/** LiveOps / RSVP payment modes (includes gate console modes). */
export type RsvpPaymentMode = "cash" | "online";
export type PaymentMode = "cash" | "upi" | "card" | "other" | "online";

export const PAYMENT_MODE_LABELS: Record<PaymentMode, string> = {
  cash: "Cash",
  upi: "UPI",
  card: "Card",
  other: "Other",
  online: "Online",
};

export type RsvpGuest = {
  id: string;
  event_id: string;
  /** Display name — required */
  name: string;
  /** Guest number (phone) — unique per event (primary identifier) */
  phone: string;
  email?: string | null;
  /** Aggregate of tier_counts */
  party_size: number;
  /** Legacy category — kept for revenue fallback */
  category: RsvpCategory;
  /** Ticket tier distribution */
  tier_counts: TierCountMap;
  /** Amount filled by client / gate (INR) — Sprint 5 */
  amount: number | null;
  /** Alias used by LiveOps guest lookup */
  amount_paid?: number | null;
  payment_mode?: RsvpPaymentMode | PaymentMode | null;
  /** Gate notes, written back by Live Ops when the entry is logged */
  notes?: string | null;
  /** Promoter name when signed up via Guest List form */
  promoter?: string | null;
  /** FK to event promoter roster (local-first until backend ships) */
  promoter_id?: string | null;
  /** Guest List approval — only approved rows appear in Live Ops */
  approved?: boolean;
  source?: GuestSource;
  /** Which list this guest is on (rsvp vs guest_list) */
  channel?: GuestChannel | null;
  instagram?: string | null;
  /** Explicit consent to receive WhatsApp messages — required before any WhatsApp send. */
  whatsapp_opt_in?: boolean;
  payment_status?: GuestPaymentStatus;
  pay_preference?: PayPreference | null;
  /** Only on the response to a paid public submit: send the guest here to pay. */
  pay_url?: string | null;
  /** Link to the guest's entry pass, when they have a valid one. */
  pass_url?: string | null;
  /** Set when staff confirm gate entry. Non-null = confirmed (hidden from Live Ops, shown on RSVP tab). */
  checked_in_at?: string | null;
  created_at: string;
  updated_at?: string | null;
};

export type RsvpGuestCreate = {
  name: string;
  phone: string;
  email?: string | null;
  party_size?: number;
  category?: RsvpCategory;
  tier_counts?: TierCountMap;
  amount?: number | null;
  amount_paid?: number | null;
  payment_mode?: RsvpPaymentMode | PaymentMode | null;
  notes?: string | null;
  promoter?: string | null;
  promoter_id?: string | null;
  approved?: boolean;
  source?: GuestSource;
  channel?: GuestChannel | null;
  instagram?: string | null;
  whatsapp_opt_in?: boolean;
  /** Paid forms: the guest's choice between paying now and at the gate. */
  pay_preference?: PayPreference | null;
};

export type RsvpGuestUpdate = Partial<RsvpGuestCreate>;

export type RsvpSettings = {
  event_id: string;
  invite_token: string;
  /** Separate token for Guest List public signup */
  guest_list_token: string;
  /** Separate token for Walk-in public signup */
  walk_in_token: string;
  category_prices: Record<RsvpCategory, number>;
  /** UTC ISO cutoff for public RSVP link (null = never) */
  rsvp_disable_at?: string | null;
  /** UTC ISO cutoff for public Guest List link (null = never) */
  guest_list_disable_at?: string | null;
  /** IST wall-clock for editor: YYYY-MM-DDTHH:mm */
  rsvp_disable_at_ist?: string | null;
  guest_list_disable_at_ist?: string | null;
  event_name?: string;
  event_date?: string;
  city?: string;
  artist?: string | null;
  ticket_template_id?: TicketTemplateId | null;
  template_config?: TicketTemplateConfig;
  /** Company default merged with event override — for display */
  resolved_ticket_template_id?: TicketTemplateId;
  /** How the RSVP and Walk-in forms take money (guest list is always free). */
  rsvp_payment?: FormPayment;
  walk_in_payment?: FormPayment;
  /** Paid RSVPs still pending this many hours before the event are rejected and refunded. */
};

export type PublicRsvpMeta = {
  event_id: string;
  event_name: string;
  event_date: string;
  city: string;
  artist?: string | null;
  invite_token: string;
  link_kind?: "rsvp" | "guest_list" | "walk_in";
  /** Ticket tiers for count inputs on public forms */
  ticket_tiers: { id: string; name: string; price: number }[];
  ticket_template_id?: TicketTemplateId;
  template_config?: TicketTemplateConfig;
  banner_url?: string | null;
  lineup?: { name: string; role?: string | null }[];
  promoters?: { id: string; name: string }[];
  /** How THIS link takes money; drives the pay choice on the form. */
  payment_mode?: FormPayment;
};

/** Patch payload for RSVP settings — IST fields clear when set to null. */
export type RsvpSettingsPatch = {
  category_prices?: Record<RsvpCategory, number>;
  rsvp_disable_at_ist?: string | null;
  guest_list_disable_at_ist?: string | null;
  ticket_template_id?: TicketTemplateId | null;
  template_config?: Partial<TicketTemplateConfig>;
  rsvp_payment?: FormPayment;
  walk_in_payment?: FormPayment;
};

/** Aggregated promoter-wide guest row for Guest List tab. */
export type PromoterGuest = {
  /** Normalized phone — unique across promoter */
  phone: string;
  name: string;
  instagram?: string | null;
  events_attended: number;
  /** Event names attended (for hover detail) */
  event_names: string[];
  favourite_artist: string | null;
};

export type WalkInGuest = {
  id: string;
  event_id: string;
  name: string;
  phone: string;
  email?: string | null;
  party_size: number;
  tier_counts: TierCountMap;
  amount: number | null;
  notes?: string | null;
  /** Explicit consent to receive WhatsApp messages — required before any WhatsApp send. */
  whatsapp_opt_in?: boolean;
  created_at: string;
  updated_at?: string | null;
};

export type WalkInGuestCreate = {
  name: string;
  phone: string;
  email?: string | null;
  party_size?: number;
  tier_counts?: TierCountMap;
  amount?: number | null;
  notes?: string | null;
  whatsapp_opt_in?: boolean;
};

export type WalkInGuestUpdate = Partial<WalkInGuestCreate>;

export function sumTierCounts(tierCounts: TierCountMap | undefined | null): number {
  if (!tierCounts) return 0;
  return Object.values(tierCounts).reduce((sum, n) => sum + (Number(n) || 0), 0);
}

export function formatTierDistribution(
  tierCounts: TierCountMap | undefined | null,
  tierNames?: Record<string, string>,
): string {
  if (!tierCounts) return "—";
  const parts = Object.entries(tierCounts)
    .filter(([, n]) => Number(n) > 0)
    .map(([id, n]) => `${tierNames?.[id] ?? id}: ${n}`);
  return parts.length ? parts.join(", ") : "—";
}

export function normalizeGuestNumber(phone: string): string {
  const cleaned = (phone || "").replace(/[\s\-()]/g, "").trim();
  const indian = cleaned.match(/^(?:\+?91|0)?([6-9]\d{9})$/);
  if (indian) return indian[1];
  return cleaned;
}

/** True when guest belongs to the Guest List channel (not public RSVP). */
export function isGuestListGuest(guest: {
  channel?: GuestChannel | string | null;
  source?: GuestSource | string | null;
}): boolean {
  return guest.channel === "guest_list" || guest.source === "guest_list";
}

/** Live Ops' Guest List panel is Guest-List-only: RSVP and Walk-in guests are
 * admitted through the Tier entry log instead. A guest shows here once
 * approved on the Guest List table, and drops off once their entry is
 * confirmed (checked_in_at set). */
export function isLiveOpsEligible(guest: RsvpGuest): boolean {
  if (guest.checked_in_at) return false;
  return isGuestListGuest(guest) && guest.approved === true;
}

/** Resolve RSVP vs Guest List membership from channel, falling back to source. */
export function resolveGuestListLabel(guest: {
  channel?: GuestChannel | string | null;
  source?: GuestSource | string | null;
}): "RSVP" | "Guest List" {
  if (guest.channel === "guest_list") return "Guest List";
  if (guest.channel === "rsvp") return "RSVP";
  if (guest.source === "guest_list") return "Guest List";
  return "RSVP";
}
